///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { GtthUpdateMainConfigCommand } from '../../logical-topology/engine/commands/project/update-main-config-command';
import { GtthAutoDesignCommand } from '../../logical-topology/engine/commands/design/auto-design-command';
import { Subject } from 'rxjs';
import { ProjectWindowsPopupService } from '../project-windows-popup.service';
import { FormGroup, NgForm } from '@angular/forms';
import { GtthAdvancedPlanTabs } from './advanced-paln-tabs.enum';
import { GtthMultipleAutoDesignCommand } from '../../logical-topology/engine/commands/design/multiple-auto-desing-command';
import { AutoSetFrequenciesDTO, DesignRingsDTO } from '../../../generated/shared-types.model';
import { GtthNotificationsService } from '../../logical-topology/engine/notifications-service';
import { GtthProjectUserSessionService } from '../../logical-topology/engine/project-user-session.service';
import { ActiveRestorableWindow } from '../active-restorable-window';
import { GtthLogicalTopology } from '../../logical-topology/entities/logical-topology';
import { _cloneDeep, parseFormDataToObj } from '../../../shared/utils';
import { GtthLayerDtoModel } from '../../../services/project-socket/dto/gtth-layer-dto.model';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AutoDesignPtMpModel } from '../../../services/logical-topology/design/auto-design-ptmp.model';
import { GtthReactiveNumber } from '../../../shared/reactive-forms/input-number';
import { GtthReactiveCheckbox } from '../../../shared/reactive-forms/input-checkbox';
import { GtthInputControlService } from '../../../shared/reactive-forms/input-control.service';
import {
    initReactiveControlsForLayer,
    updateReactiveFormForLayer,
    updateTunnelAndRedundancyFactorFields
} from '../design-dialogs-shared-utils';
import { GtthReactiveDropdown } from '../../../shared/reactive-forms/input-dropdown';
import { GtthShareComponentsModule } from '../../../shared/shared-components.module';
import { SuiSvgIconComponent, SuiTranslatePipe } from '@siklu/sui-lib';
import { TabsNavComponent } from '../../../shared/tabs-nav/tabs-nav.component';
import { GtthReactiveFormInputComponent } from '../../../shared/reactive-forms/reactive-form-input.component';
import { ButtonsDirectivesModule } from '../../../shared/directives/buttons-directives/buttons-directives.module';
import { GtthNumberInputValidationDirective } from '../../../shared/directives/validation-directives/number-input.directive';

@Component({
    selector: 'winde-plan-advanced-config',
    templateUrl: './advanced-config.component.html',
    styleUrls: ['./advanced-plan-config.scss'],
    imports: [
        GtthShareComponentsModule,
        MatDialogModule,
        ButtonsDirectivesModule,
        SuiTranslatePipe,
        SuiSvgIconComponent,
        TabsNavComponent,
        GtthReactiveFormInputComponent,
        GtthNumberInputValidationDirective
    ]
})
export class GtthAdvancedConfigComponent extends ActiveRestorableWindow implements OnInit, OnDestroy {
    @ViewChild('editFormAdvancedConfig', { static: true }) editFormAdvancedConfig: NgForm;

    public showFormValidationAlert: boolean;
    public alertMessageSubject: Subject<string>;

    public totalNumberOfNodes: number;
    public layerContainsMhTgBu: boolean[] = [];
    public redundancyEnabled: boolean[] = [];
    public resiliencyEnabled: boolean[] = [];
    public redundancyPercent: number[] = [];
    public nodesCountPerLayer: number[] = [];
    public advancedPlanTabs = GtthAdvancedPlanTabs;
    public readonly tabsToShow = [GtthAdvancedPlanTabs[GtthAdvancedPlanTabs.GENERAL], GtthAdvancedPlanTabs[GtthAdvancedPlanTabs.P2MP]];
    public currentTab = GtthAdvancedPlanTabs.GENERAL;

    public layerNames: string[] = [];
    public activeLayerTab = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
    public copyDesignSettingsLayer = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;

    public designDirection: boolean;
    public editForm: FormGroup;
    public capFacPerLayer: GtthReactiveNumber[] = [];
    public optBsFacPerLayer: GtthReactiveNumber[] = [];
    public costFacPerLayer: GtthReactiveNumber[] = [];
    public lcentDistPerLayer: GtthReactiveNumber[] = [];
    public limBoxesPerLayer: GtthReactiveCheckbox[] = [];
    public nCandWinPerLayer: GtthReactiveNumber[] = [];
    public peerBsFacPerLayer: GtthReactiveNumber[] = [];
    public p2pDistFacPerLayer: GtthReactiveNumber[] = [];
    public redunFacPerLayer: GtthReactiveNumber[] = [];
    public tgMaxIttrPerLayer: GtthReactiveNumber[] = [];
    public takeRatePerLayer: GtthReactiveNumber[] = [];
    public tunnelFacPerLayer: GtthReactiveNumber[] = [];
    public asMethodPerLayer: GtthReactiveDropdown[] = [];
    public maxDevNumPerLayer: GtthReactiveDropdown[] = [];
    public popDistFacPerLayer: GtthReactiveNumber[] = [];
    private originDesignPtmp: AutoDesignPtMpModel[];
    private originDesignRings: DesignRingsDTO[];
    private originAutoSetFrequencies: AutoSetFrequenciesDTO[];

    constructor(
        public dialogRef: MatDialogRef<GtthAdvancedConfigComponent>,
        public historyService: GtthHistoryService,
        private readonly notificationService: GtthNotificationsService,
        public readonly projectUserSessionService: GtthProjectUserSessionService,
        private readonly inputControlService: GtthInputControlService
    ) {
        super();
        this.totalNumberOfNodes = this.historyService.logicalTopology.getTerminalStationsCount();
        this.alertMessageSubject = new Subject();
    }

    ngOnInit() {
        if (!this.historyService.isEditModeEnabled) {
            this.clear();
            return;
        }

        this.layerNames = [];
        if (this.historyService.logicalTopology.layersModeEnabled) {
            this.historyService.logicalTopology.layers.forEach((layer: GtthLayerDtoModel, layerIdx: number) => {
                this.layerContainsMhTgBu[layerIdx] = this.historyService.logicalTopology.selectedProductsContainsMhTgBu(
                    this.historyService.projectProductsService,
                    layerIdx
                );
                this.resiliencyEnabled[layerIdx] =
                    this.historyService.logicalTopology.autodesignP2mpPerLayer[layer.layerIndex].isResiliencyEnabled();
                this.initReactiveControlsForLayer(layerIdx, this.layerContainsMhTgBu[layerIdx]);
                this.layerNames.push(layer.getFullName());
                this.nodesCountPerLayer[layerIdx] = this.getNodesPerLayerCount(layerIdx);
                this.setRedundancyEnabled(layerIdx, this.historyService.logicalTopology.designRingsPerLayer[layer.layerIndex].rsz_th > 0);
            });
            this.initForm(GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX);
            this.changeLayerTab(this.layerNames[0]);
        } else {
            this.layerContainsMhTgBu[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX] =
                this.historyService.logicalTopology.selectedProductsContainsMhTgBu(
                    this.historyService.projectProductsService,
                    GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX
                );
            this.resiliencyEnabled[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX] =
                this.historyService.logicalTopology.autodesignP2mpPerLayer[
                    GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX
                ].isResiliencyEnabled();
            this.initReactiveControlsForLayer(
                GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX,
                this.layerContainsMhTgBu[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX]
            );
            this.initForm(GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX);

            this.nodesCountPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX] = this.getNodesPerLayerCount(
                GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX
            );
            this.setRedundancyEnabled(
                GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX,
                this.historyService.logicalTopology.designRingsPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX].rsz_th > 0
            );
        }

        this.designDirection =
            this.historyService.logicalTopology.autodesignP2mpPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX].bottom_up;

        this.originDesignPtmp = _cloneDeep(this.historyService.logicalTopology.autodesignP2mpPerLayer);
        this.originDesignRings = _cloneDeep(this.historyService.logicalTopology.designRingsPerLayer);
        this.originAutoSetFrequencies = _cloneDeep(this.historyService.logicalTopology.autoSetFrequenciesPerLayer);
    }

    ngOnDestroy() {
        if (this.alertMessageSubject) {
            this.alertMessageSubject.complete();
        }
    }

    changeTab(tabIndex: string) {
        this.currentTab = GtthAdvancedPlanTabs[tabIndex];
        if (this.currentTab === GtthAdvancedPlanTabs.P2MP) {
            this.initReactiveControlsForLayer(this.activeLayerTab, this.layerContainsMhTgBu[this.activeLayerTab]);
            this.initForm(this.activeLayerTab);
        }
        this.resiliencyEnabled[this.activeLayerTab] =
            this.historyService.logicalTopology.autodesignP2mpPerLayer[this.activeLayerTab].isResiliencyEnabled();
    }

    onWorstCaseChange(activeLayerTab: number, wc_analysis: boolean): void {
        this.historyService.logicalTopology.autoSetFrequenciesPerLayer[activeLayerTab].explicit_only = !wc_analysis;
    }

    setRedundancyEnabled(layerIndex: number, newValue: boolean): void {
        this.redundancyEnabled[layerIndex] = newValue;

        if (!newValue) {
            // clear redundancy data
            this.clearRedundancy(layerIndex);
        } else {
            if (!this.historyService.logicalTopology.designRingsPerLayer[layerIndex].rsz_th) {
                this.historyService.logicalTopology.designRingsPerLayer[layerIndex].rsz_th =
                    this.nodesCountPerLayer[layerIndex] > 10 ? 10 : this.nodesCountPerLayer[layerIndex];
            }

            this.calculateRedundancy(layerIndex, this.historyService.logicalTopology.designRingsPerLayer[layerIndex].rsz_th);
            this.historyService.logicalTopology.designRingsPerLayer[layerIndex].rsz_weig = 1;
        }
    }

    onResiliencyEnabledChange(isResiliencyEnabled: boolean, layerIndex: number): void {
        this.resiliencyEnabled[layerIndex] = isResiliencyEnabled;
        updateTunnelAndRedundancyFactorFields(this, layerIndex);
    }

    calculateRedundancy(activeLayerTab: number, value: number, isCalculateThreshold = false): void {
        const nodesCount = this.nodesCountPerLayer[activeLayerTab];
        if (isCalculateThreshold) {
            this.historyService.logicalTopology.designRingsPerLayer[activeLayerTab].rsz_th = Math.round((value * nodesCount) / 100);
        } else {
            const res = (value / nodesCount) * 100;
            this.redundancyPercent[activeLayerTab] = Math.round(res * 10) / 10;
        }
    }

    startPlanning() {
        if (this.editForm.invalid) {
            this.inputControlService.formSubmitEventBroadcast(this.editForm);
            return false;
        }
        if (this.editFormAdvancedConfig.form.invalid) {
            this.alertMessageSubject.next('error.formRequiredFields');
            return;
        }

        this.alertMessageSubject.next('');

        if (!this.historyService.logicalTopology.hasPopStationsInTopmostLayer()) {
            this.notificationService.showError('No POP found, or POP not connected. No optimization performed');
            this.clear();
            return;
        }

        if (
            this.historyService.logicalTopology.selectedProductsContainsMhTgBu(this.historyService.projectProductsService) &&
            !this.historyService.logicalTopology.getIsIpConfigDefined()
        ) {
            this.notificationService.showError(
                'In order to complete design, please specify the "Start IP address", "Subnet mask width" and "Default GW IP" fields in Project settings -> NW config tab'
            );
            this.clear();
            return;
        }

        if (!this.historyService.logicalTopology.exportLinksConfig.checkGateWayValidityBySubnetwork()) {
            this.notificationService.showError('In order to complete design, "Default GW IP" must belong to subnetwork');
            this.clear();
            return;
        }
        parseFormDataToObj(this.historyService.logicalTopology.autodesignP2mpPerLayer[this.activeLayerTab], this.editForm);

        let designCommand;

        if (this.projectUserSessionService.isAutoRepeatDesign) {
            designCommand = this.historyService.command(GtthMultipleAutoDesignCommand).create();
        } else {
            designCommand = this.historyService.command(GtthAutoDesignCommand).create();
        }

        this.historyService.doCommand(designCommand);
        this.dialogRef.close('cancel');
    }

    clear() {
        if (this.editFormAdvancedConfig.form.invalid) {
            this.historyService.logicalTopology.autodesignP2mpPerLayer = this.originDesignPtmp;
            this.historyService.logicalTopology.designRingsPerLayer = this.originDesignRings;
            this.historyService.logicalTopology.autoSetFrequenciesPerLayer = this.originAutoSetFrequencies;
        }

        if (this.editForm.invalid) {
            this.historyService.logicalTopology.autodesignP2mpPerLayer = this.originDesignPtmp;
        } else {
            parseFormDataToObj(this.historyService.logicalTopology.autodesignP2mpPerLayer[this.activeLayerTab], this.editForm);
        }

        if (this.historyService.isEditModeEnabled) {
            this.historyService.doCommand(this.historyService.command(GtthUpdateMainConfigCommand));
        }
        this.dialogRef.close('cancel');
    }

    changeLayerTab(tabName: string) {
        parseFormDataToObj(this.historyService.logicalTopology.autodesignP2mpPerLayer[this.activeLayerTab], this.editForm);

        this.activeLayerTab = this.layerNames.indexOf(tabName);
        this.updateReactiveControlsValueForLayer(this.activeLayerTab);
    }

    changeDesignDirection() {
        this.historyService.logicalTopology.autodesignP2mpPerLayer.forEach((config) => {
            config.bottom_up = this.designDirection;
        });
        this.historyService.logicalTopology.designRingsPerLayer.forEach((config) => {
            config.bottom_up = this.designDirection;
        });
    }

    isRestorable(): boolean {
        return true;
    }

    designRequired(): boolean {
        return false;
    }

    copyDesignSettingsFromLayer(currentTab: number) {
        if (currentTab === this.advancedPlanTabs.GENERAL) {
            //    copy designRingsPerLayer & autoSetFrequenciesPerLayer settings from selected layer to current
            Object.keys(this.historyService.logicalTopology.designRingsPerLayer[this.activeLayerTab]).forEach((key: string) => {
                this.historyService.logicalTopology.designRingsPerLayer[this.activeLayerTab][key] =
                    this.historyService.logicalTopology.designRingsPerLayer[this.copyDesignSettingsLayer][key];
            });
            Object.keys(this.historyService.logicalTopology.autoSetFrequenciesPerLayer[this.activeLayerTab]).forEach((key: string) => {
                this.historyService.logicalTopology.autoSetFrequenciesPerLayer[this.activeLayerTab][key] =
                    this.historyService.logicalTopology.autoSetFrequenciesPerLayer[this.copyDesignSettingsLayer][key];
            });
        }
        if (currentTab === this.advancedPlanTabs.P2MP) {
            //    copy autodesignP2mpPerLayer settings from selected layer to current
            Object.keys(this.historyService.logicalTopology.autodesignP2mpPerLayer[this.activeLayerTab]).forEach((key: string) => {
                this.historyService.logicalTopology.autodesignP2mpPerLayer[this.activeLayerTab][key] =
                    this.historyService.logicalTopology.autodesignP2mpPerLayer[this.copyDesignSettingsLayer][key];
            });
            this.updateReactiveControlsValueForLayer(this.activeLayerTab);
        }
    }

    private getNodesPerLayerCount(activeLayerTab: number): number {
        const stationsIterator = this.historyService.logicalTopology.getStationsIterator();

        return Array.from(stationsIterator).filter((station) => !station.isPop() && station.getLayerIndex() === activeLayerTab).length;
    }

    private clearRedundancy(layer: number): void {
        this.historyService.logicalTopology.designRingsPerLayer[layer].rsz_th = 0;
        this.historyService.logicalTopology.designRingsPerLayer[layer].rsz_weig = 0;
    }

    private initReactiveControlsForLayer(layerIdx: number, layerHasMhTG: boolean): void {
        initReactiveControlsForLayer(this, layerIdx, layerHasMhTG);
    }

    private updateReactiveControlsValueForLayer(layerIdx: number): void {
        updateReactiveFormForLayer(this, layerIdx);
        this.initForm(layerIdx);
    }

    private initForm(layerIdx: number): void {
        this.editForm = this.inputControlService.toFormGroup([
            this.capFacPerLayer[layerIdx],
            this.costFacPerLayer[layerIdx],
            this.optBsFacPerLayer[layerIdx],
            this.lcentDistPerLayer[layerIdx],
            this.limBoxesPerLayer[layerIdx],
            this.nCandWinPerLayer[layerIdx],
            this.redunFacPerLayer[layerIdx],
            this.p2pDistFacPerLayer[layerIdx],
            this.peerBsFacPerLayer[layerIdx],
            this.tunnelFacPerLayer[layerIdx],
            this.tgMaxIttrPerLayer[layerIdx],
            this.takeRatePerLayer[layerIdx],
            this.popDistFacPerLayer[layerIdx],
            this.asMethodPerLayer[layerIdx],
            this.maxDevNumPerLayer[layerIdx]
        ]);
    }
}

@Component({
    selector: 'winde-project-gtth-advanced-plan-config-popup',
    template: '',
    standalone: false
})
export class ProjectGtthAdvancedPlanConfigPopupComponent implements OnInit {
    constructor(private readonly projectWindowsPopupService: ProjectWindowsPopupService) {}

    ngOnInit() {
        this.projectWindowsPopupService.openWindow(GtthAdvancedConfigComponent, 'lg');
    }
}
